import React, { useState } from "react";
import csv_icon from "../../../../public/images/csv-export-icon.png";
import excel_icon from "../../../../public/images/xls-export-icon.png";
import pdf_icon from "../../../../public/images/pdf-icon.png";
import ms_word_icon from "../../../../public/images/ms-word-icon.png";
import zip_icon from "../../../../public/images/file-zipper-regular.svg";
import unknown_file from "../../../../public/images/UnknownFile.png";
import { DataService } from "../../../../shared/services/dataService";
import { ReportType } from "../../../../shared/types/enums";

const FILE_ICONS = {
    csv: csv_icon,
    xls: excel_icon,
    xlsx: excel_icon,
    pdf: pdf_icon,
    doc: ms_word_icon,
    docx: ms_word_icon,
    zip: zip_icon,
    default: unknown_file,
};

const assignIcon = (fileType) => FILE_ICONS[fileType] || FILE_ICONS.default;

const DownloadReportViaImageCellRenderer = ({ value, data, context, isCompanionCol }) => {
    const isCompanionColumn = typeof isCompanionCol === "function" ? isCompanionCol() : false;
    const showIcon = isCompanionCol ?
        data['isCompanionSave'] && data['genStatus'] === "Completed"
        : data['genStatus'] === "Completed";

    const isComplianceSave = data.isComplianceSave;
    const [loadingFiles, setLoadingFiles] = useState({});

    const invokeParentMethod = async (_fileType = "", _fileExtension = "") => {
        let fileExtension = !DataService.isStringNullOrEmpty(_fileExtension) ? _fileExtension : value;
        // Set loading state for this file
        setLoadingFiles((prev) => ({ ...prev, [`${_fileType}-${fileExtension}`]: true }));

        try {
            let blob = await context.componentParent.handleDownloadAg(data, fileExtension, _fileType);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${data.reportDetailName}${(_fileType == "Companion" ? "PorzioAnalysisReport" : _fileType)}.${fileExtension}`); //or any other extension
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            // Reset loading state after download completes
            setLoadingFiles((prev) => ({ ...prev, [`${_fileType}-${fileExtension}`]: false }));
        }
    };

    const renderDownloadIcons = (categories) => {
        return categories.map(({ type, fileExt, title }, index) => {
            const isLoading = loadingFiles[`${type}-${fileExt}`]; // Check if file is downloading
            return (
                <span key={index} style={{ paddingRight: "5px", cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.5 : 1 }}
                    onClick={!isLoading ? () => invokeParentMethod(type, fileExt) : null}>
                    <img src={assignIcon(fileExt || value)} title={title} alt={title} style={{ marginBottom: "25px", width: "25px", height: "25px" }} />
                </span>
            );
        });
    };

    const renderIconsByCategory = (category) => {
        const translatedReports = data.translatedReportLanguageList?.trim()
            ? data.translatedReportLanguageList.split(",").filter(item => item.trim() !== "")
            : [];

        //We have assumed Companion and FVM file will always be just one file (mostl likely an .xlsx file). 
        // We need to split and handle if this changes in future
        const displayFileTypes = JSON.parse(data.displayFileTypes);

        switch (category) {
            case "Companion": //Companion + FVM 
                return renderDownloadIcons([
                    {
                        type: "Companion", fileExt: displayFileTypes?.COMPANION ?? value,
                        title: `Porzio Analysis Report - ${displayFileTypes?.COMPANION ?? value} version`
                    },
                    { type: "FVM", fileExt: displayFileTypes?.FVMMAPPING ?? value, title: `FVM Report - ${displayFileTypes?.FVMMAPPING ?? value} version` },
                ]);
            case "US_General_Report":
                //We have assumed Compliance file will not be split into multiple files (if more than 250K records) and so have 
                // not yet used the displayFileTypes for getting the file type value here from DB.
                return isComplianceSave && renderDownloadIcons([
                    { type: "", fileExt: "xlsx", title: "General Report - excel version" },
                    { type: "", fileExt: "csv", title: "General Report - csv version" },
                    { type: "Research", fileExt: "xlsx", title: "Research Report - excel version" },
                    { type: "Research", fileExt: "csv", title: "Research Report - csv version" },
                ]);

            case "Compliance":
                let complianceFileTypes = displayFileTypes?.COMPLIANCE?.split(",") ?? [value];
                return (
                    <>
                        {isComplianceSave && complianceFileTypes.map((dispFileType, index) =>
                            renderDownloadIcons([{ type: "", fileExt: dispFileType, title: `English Report - ${dispFileType} version` }]))}
                        {translatedReports.map((lang, index) =>
                            renderDownloadIcons([{ type: lang.replace("/", "-"), fileExt: value, title: `${lang} Report` }])
                        )}
                    </>);
            case "Standard":
                return renderDownloadIcons([{ type: "", fileExt: displayFileTypes?.COMPLIANCE ?? value, title: "Standard Report" }]);

            default:
                return null;
        }
    };

    const getReportCategory = (reportType) => {
        switch (reportType) {
            case ReportType.US_GENERAL:
                return (isCompanionColumn ? "Companion" : "US_General_Report");
            case ReportType.US_OWNERSHIP:
                return (isCompanionColumn ? null : "US_Ownership_Report");
            case ReportType.US_STATE:
            case ReportType.OUS:
                return (isCompanionColumn ? "Companion" : "Compliance");
            case ReportType.STANDARD:
                return "Standard";
        }
    }

    if (!showIcon) return null;

    let reportCategory = getReportCategory(data.reportType);

    return reportCategory ? <>{renderIconsByCategory(reportCategory)}</> : null;
};

export default DownloadReportViaImageCellRenderer;
